// ── AssistantHistoryPage — historial de conversaciones con Evo Brain ────────
// Pedido de Eduardo (2026-08-06): "hoy solo veo la conversación de la
// pantalla donde estoy parado". Lista todas + busca por contenido — mismo
// List Pattern que Reports/Tasks, sin pattern nuevo (List Pattern § DESIGN_
// SYSTEM.md). Sin entrada en el sidebar a propósito (mismo criterio que
// /showcase) — se llega desde el botón de historial en el dock.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTopBarCtx } from '../contexts/TopBarContext.js';
import { FilterRow } from '../components/ui/FilterRow.js';
import { ListItem } from '../components/ui/ListItem.js';
import { Pill } from '../components/PillGroup.js';
import { EmptyState } from '../components/ui/EmptyState.js';
import { ListState } from '../components/ui/ListState.js';
import { LoadingState } from '../components/ui/LoadingState.js';
import { TimeBandedList } from '../components/common/TimeBandedList.js';
import { parseServerDate } from '../lib/date.js';
import { entityTypeNavKey } from '../lib/assistantModules.js';
import {
  useConversationsList, useHistorySearch,
  type ConversationSummary, type HistorySearchHit,
} from '../hooks/useAssistantHistory.js';

function fmt(iso: string): string {
  return parseServerDate(iso).toLocaleString('es', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export function AssistantHistoryPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');
  const isSearching = search.trim().length > 0;

  const { data: listData, isLoading: listLoading, isError: listError, error: listErrorObj, refetch: listRefetch } = useConversationsList();
  const { data: searchData, isFetching: searchLoading } = useHistorySearch(search);

  const { setOverride } = useTopBarCtx();
  useEffect(() => {
    setOverride({ title: t('assistantHistory.pageTitle'), heroIcon: 'activity', heroIconColor: 'var(--evo-steel)' });
    return () => setOverride({});
  }, [setOverride, t]);

  const allConversations = listData?.conversations ?? [];
  const allHits = searchData?.hits ?? [];

  // Opciones del filtro: solo módulos que realmente aparecen en el historial.
  const moduleOptions = useMemo(() => {
    const types = new Set((isSearching ? allHits : allConversations).map(c => c.entityType));
    return Array.from(types)
      .map(entityType => ({ value: entityType, label: entityTypeNavKey(entityType) ? t(entityTypeNavKey(entityType)) : entityType }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [isSearching, allHits, allConversations, t]);

  const conversations = moduleFilter ? allConversations.filter(c => c.entityType === moduleFilter) : allConversations;
  const hits = moduleFilter ? allHits.filter(h => h.entityType === moduleFilter) : allHits;
  const isLoading = isSearching ? searchLoading : listLoading;

  function openConversation(href: string | null) {
    if (href) navigate(href);
  }

  return (
    <div className="flex flex-col h-full animate-fade-blur-in">
      <FilterRow>
        <FilterRow.Search
          value={search}
          onChange={setSearch}
          onSubmit={() => {}}
          onClear={() => setSearch('')}
          placeholder={t('assistantHistory.searchPlaceholder')}
          fullWidth
        />
        <FilterRow.Dropdown<string>
          value={moduleFilter}
          placeholder={t('assistantHistory.allModules')}
          options={[
            { value: '', label: t('assistantHistory.allModules') },
            ...moduleOptions,
          ]}
          onChange={val => setModuleFilter(val ?? '')}
        />
      </FilterRow>

      <div className="flex-1 overflow-auto">
        {isLoading && <LoadingState size="sm" />}

        {!isLoading && isSearching && hits.length === 0 && (
          <EmptyState size="md" reserveSpace title={t('assistantHistory.noResults', { query: search })} />
        )}
        {!isLoading && !isSearching && conversations.length === 0 && (
          <ListState
            isLoading={false} isError={listError} error={listErrorObj}
            isEmpty emptyTitle={t('assistantHistory.empty')}
            onRetry={() => { void listRefetch(); }}
          >{null}</ListState>
        )}

        {!isLoading && isSearching && hits.length > 0 && (
          <TimeBandedList<HistorySearchHit, number>
            items={hits}
            getId={h => h.messageId}
            getTimestamp={h => h.createdAt}
            checkedIds={new Set()}
            renderItem={hit => (
              <ListItem
                key={hit.messageId}
                itemId={hit.messageId}
                title={hit.entityLabel}
                subtitle={hit.snippet}
                subtitleLines={2}
                meta={fmt(hit.createdAt)}
                badges={[
                  <Pill key="role" size="xs" tone={hit.role === 'user' ? 'cyan' : 'muted'}>
                    {t(`assistantHistory.role.${hit.role}`, { defaultValue: hit.role })}
                  </Pill>,
                ]}
                onClick={() => openConversation(hit.entityHref)}
              />
            )}
          />
        )}

        {!isLoading && !isSearching && conversations.length > 0 && (
          <TimeBandedList<ConversationSummary, number>
            items={conversations}
            getId={c => c.id}
            getTimestamp={c => c.lastMessageAt}
            checkedIds={new Set()}
            renderItem={conv => (
              <ListItem
                key={conv.id}
                itemId={conv.id}
                title={conv.entityLabel}
                {...(conv.preview ? { subtitle: conv.preview } : {})}
                subtitleLines={1}
                meta={fmt(conv.lastMessageAt)}
                badges={[
                  <Pill key="count" size="xs" tone="muted">
                    {t('assistantHistory.messageCount', { count: conv.messageCount })}
                  </Pill>,
                ]}
                onClick={() => openConversation(conv.entityHref)}
              />
            )}
          />
        )}
      </div>
    </div>
  );
}
